// Cookie notice. The choice is shared with the WorkBrawl hub and the other titles (same
// origin, same localStorage keys), so whoever answered it anywhere is not asked again. It is
// shown here too because many people arrive straight at a room link and never see the hub.
//
// Two forms, picked by the region the Google tag snippet in index.html resolves:
// - "strict" (EU/EEA/UK/CH, or unknown): accept or reject; analytics stays off until accepted.
// - "open" (everywhere else): analytics is on, and this is an informational notice with a
//   single "Got it". Rejecting stays possible with "Cookie settings" in the hub.

import { h } from "./dom";
import { LANG } from "./i18n";
import { STRINGS } from "./strings";

/** Read by the inline Google tag snippet in index.html and by the hub: keep the name in sync. */
const CONSENT_KEY = "workbrawl:consent";
/** Closing the notice (X) hides it for this tab only; it asks again on the next visit. */
const DISMISSED_KEY = "workbrawl:consent-dismissed";
/** The informational notice was acknowledged (not a consent choice). Shared with the hub. */
const NOTICE_KEY = "workbrawl:notice-seen";
const PRIVACY_URL = LANG === "es" ? "/es/privacidad/" : "/privacy/";

declare global {
  interface Window {
    /** Set by the Google tag snippet in index.html. */
    workbrawlRegion?: Promise<"open" | "strict">;
  }
}

let banner: HTMLElement | null = null;

/** Shows the right notice when there is no stored choice yet. */
export function initConsent(): void {
  const choice = read(localStorage, CONSENT_KEY);
  if (choice === "granted" || choice === "denied") return;
  void (window.workbrawlRegion ?? Promise.resolve("strict")).then((region) => {
    if (region === "open") {
      if (read(localStorage, NOTICE_KEY) !== "1") showNotice();
    } else if (read(sessionStorage, DISMISSED_KEY) !== "1") {
      showBanner();
    }
  });
}

function showNotice(): void {
  if (banner) return;
  const ok = h("button", { type: "button", class: "btn btn-primary btn-small" }, STRINGS.consentGotIt);
  banner = h(
    "section",
    { class: "consent", role: "region", "aria-label": "Cookies" },
    h("p", {}, STRINGS.consentNotice, " ", h("a", { href: PRIVACY_URL }, STRINGS.privacy)),
    h("div", { class: "consent-actions" }, ok),
  );
  ok.addEventListener("click", () => {
    write(localStorage, NOTICE_KEY, "1");
    hide();
  });
  document.body.append(banner);
}

function showBanner(): void {
  if (banner) return;
  const close = h(
    "button",
    { type: "button", class: "consent-close", "aria-label": STRINGS.close, title: STRINGS.close },
    "\u00d7",
  );
  const accept = h("button", { type: "button", class: "btn btn-primary btn-small" }, STRINGS.consentAccept);
  const reject = h("button", { type: "button", class: "btn btn-small" }, STRINGS.consentReject);
  banner = h(
    "section",
    { class: "consent", role: "region", "aria-label": "Cookies" },
    close,
    h("p", {}, STRINGS.consentText, " ", h("a", { href: PRIVACY_URL }, STRINGS.privacy)),
    // Equal prominence for both choices: same size, side by side.
    h("div", { class: "consent-actions" }, reject, accept),
  );
  // Closing is not consent: analytics stays denied (Consent Mode default).
  close.addEventListener("click", () => {
    write(sessionStorage, DISMISSED_KEY, "1");
    hide();
  });
  accept.addEventListener("click", () => decide(true));
  reject.addEventListener("click", () => decide(false));
  document.body.append(banner);
}

function hide(): void {
  banner?.remove();
  banner = null;
}

function decide(granted: boolean): void {
  write(localStorage, CONSENT_KEY, granted ? "granted" : "denied");
  try {
    window.gtag?.("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
  } catch {
    // Analytics must never break the app.
  }
  if (!granted) deleteAnalyticsCookies();
  hide();
}

/** Rejecting also removes Analytics cookies already set (on this host and its parent). */
function deleteAnalyticsCookies(): void {
  const names = document.cookie
    .split(";")
    .map((c) => c.split("=")[0]!.trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  const host = location.hostname;
  const domains = ["", host, `.${host.split(".").slice(-2).join(".")}`];
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ""}`;
    }
  }
}

// Storage can be unavailable (private mode, blocked site data): the notice then simply
// shows on every visit and analytics stays off.
function read(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function write(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // See read().
  }
}
